import { useNavigation, useRoute } from '@react-navigation/native';
import { useEffect, useState } from 'react';
import useStableCallback from 'src/hooks/useCallbackUtils/useStableCallback';
export function resolveInitialActiveIndex(tabs, tabID) {
    if (!tabID || tabs.length === 0) {
        return 0;
    }
    const matchedIndex = tabs.findIndex(tab => tab.id === tabID);
    return matchedIndex >= 0 ? matchedIndex : 0;
}
export default function useTabs(options) {
    const navigation = useNavigation();
    const route = useRoute();
    const routeParams = route.params;
    const routeTabID = routeParams?.tabID;
    const initialTabs = options?.tabs ?? [];
    const [state, setState] = useState(() => ({
        tabs: initialTabs,
        activeIndex: resolveInitialActiveIndex(initialTabs, routeTabID),
    }));
    useEffect(() => {
        const tab = state.tabs[state.activeIndex];
        if (!tab) {
            return;
        }
        if (!routeTabID) {
            navigation.setParams({ tabID: tab.id });
        }
    }, [routeTabID]);
    useEffect(() => {
        if (state.tabs.length === 0) {
            return;
        }
        const currentTab = state.tabs[state.activeIndex];
        if (routeTabID &&
            currentTab?.id !== routeTabID) {
            const nextIndex = resolveInitialActiveIndex(state.tabs, routeTabID);
            if (nextIndex !== state.activeIndex) {
                setState(prev => ({ ...prev, activeIndex: nextIndex }));
            }
        }
    }, [state.tabs, routeTabID, state.activeIndex]);
    useEffect(() => {
        if (state.tabs.length === 0) {
            return;
        }
        if (state.activeIndex < 0 || state.activeIndex >= state.tabs.length) {
            const nextIndex = resolveInitialActiveIndex(state.tabs, routeTabID);
            setState(prev => ({ ...prev, activeIndex: nextIndex }));
        }
    }, [state.tabs, state.activeIndex, routeTabID]);
    const setActiveIndex = useStableCallback((index) => {
        setState(prev => {
            const nextTab = prev.tabs[index];
            if (!nextTab) {
                return prev;
            }
            navigation.setParams({ tabID: nextTab.id });
            return { ...prev, activeIndex: index };
        });
    });
    const setTabs = useStableCallback((tabs) => {
        setState(prev => {
            let activeIndex = prev.activeIndex;
            if (tabs.length === 0) {
                activeIndex = -1;
            }
            else if (activeIndex < 0 || activeIndex >= tabs.length) {
                activeIndex = resolveInitialActiveIndex(tabs, routeTabID);
            }
            else if (routeTabID) {
                activeIndex = resolveInitialActiveIndex(tabs, routeTabID);
            }
            const activeTab = tabs[activeIndex];
            if (activeTab && !routeTabID) {
                navigation.setParams({ tabID: activeTab.id });
            }
            return { tabs, activeIndex };
        });
    });
    const setTabCount = useStableCallback((tabID, count) => {
        setState(prevState => {
            const matchedTab = prevState.tabs.find(tab => tab.id === tabID);
            if (!matchedTab) {
                return prevState;
            }
            matchedTab.count = count;
            return { ...prevState };
        });
    });
    const actions = {
        setActiveIndex,
        setTabs,
        setTabCount,
    };
    return [state, actions];
}
